import { ArrowRight, FileText, Images, Link as Link2, PlayCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

import { MediaFrame } from '@/components/motion';
import { countAttachments, type NewsArticle } from '@/data/news';

/**
 * NewsCard — a single article tile for the /news grid.
 *
 * The whole card is a react-router <Link> to the article's detail page, styled
 * with the house plain hover-lift idiom (border/shadow/lift on hover) applied to
 * a Link rather than a div. A media area sits on top: cover photos crop via
 * MediaFrame, while contain assets (certificates, badges, logos) are shown whole
 * on a muted mat so they are never cropped. The body carries a category · date
 * meta row, a clamped title and excerpt, and a footer pairing attachment-count
 * chips with a "Read more" affordance.
 *
 * Reduced-motion safe; every fact comes from '@/data/news', nothing invented.
 */
export function NewsCard({ article }: { article: NewsArticle }) {
  const imageCount = countAttachments(article, 'image');
  const videoCount = countAttachments(article, 'video');
  const pdfCount = countAttachments(article, 'pdf');
  const linkCount = countAttachments(article, 'link');

  return (
    <Link
      to={`/news/${article.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none motion-reduce:hover:translate-y-0"
    >
      {/* ── Media ────────────────────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-t-2xl">
        {article.heroFit === 'cover' ? (
          <MediaFrame
            image={article.heroImage}
            alt={article.title}
            aspect="aspect-[16/10]"
            overlay={article.heroFit === 'cover' ? 15 : 0}
            rounded="rounded-none"
            reveal={false}
          />
        ) : (
          <div className="flex aspect-[16/10] items-center justify-center bg-muted/40 p-6">
            <img
              src={article.heroImage}
              alt={article.title}
              loading="lazy"
              className="max-h-full max-w-full object-contain"
            />
          </div>
        )}
      </div>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-1 flex-col p-6">
        <div className="flex items-center gap-2 text-xs">
          <span className="font-label tracking-[0.18em] text-brand-gold uppercase">
            {article.category}
          </span>
          <span aria-hidden className="text-muted-foreground">
            ·
          </span>
          <time dateTime={article.dateISO} className="text-muted-foreground">
            {article.dateDisplay}
          </time>
        </div>

        <h3 className="mt-3 line-clamp-2 font-display text-lg leading-snug font-semibold text-foreground transition-colors group-hover:text-brand-gold">
          {article.title}
        </h3>

        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
          {article.excerpt}
        </p>

        {/* ── Footer: attachment meta · read-more affordance ───────────────── */}
        <div className="mt-auto flex items-center justify-between gap-4 pt-4">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {imageCount > 0 && (
              <span className="flex items-center gap-1">
                <Images aria-hidden className="size-3.5" />
                <span>{imageCount}</span>
              </span>
            )}
            {videoCount > 0 && (
              <span className="flex items-center gap-1">
                <PlayCircle aria-hidden className="size-3.5" />
                <span>Film</span>
              </span>
            )}
            {pdfCount > 0 && (
              <span className="flex items-center gap-1">
                <FileText aria-hidden className="size-3.5" />
                <span>PDF</span>
              </span>
            )}
            {linkCount > 0 && (
              <span className="flex items-center gap-1">
                <Link2 aria-hidden className="size-3.5" />
                <span>{linkCount}</span>
              </span>
            )}
          </div>

          <span className="flex items-center gap-1.5 font-label text-xs tracking-[0.12em] text-brand-navy uppercase">
            Read more
            <ArrowRight
              aria-hidden
              className="size-4 transition-transform duration-300 group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
            />
          </span>
        </div>
      </div>
    </Link>
  );
}
